import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
	apiDelete,
	apiGet,
	apiPost,
	apiPut,
} from "../../lib/axiosInstance";
import { logger } from "../../lib/utils";

const buildQueryString = (params = {}) => {
	const searchParams = new URLSearchParams();

	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== "" && value !== "All") {
			searchParams.append(key, value);
		}
	});

	return searchParams.toString();
};
export const updateGameBuild = createAsyncThunk(
	"games/updateGameBuild",
	async ({ id, formData }, thunkAPI) => {
		try {
			thunkAPI.dispatch(setUploadProgress(0));

			return await apiPut("/games/" + id + "/build", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
				onUploadProgress: (event) => {
					const progress = Math.round((event.loaded * 100) / (event.total || 1));
					thunkAPI.dispatch(setUploadProgress(progress));
				},
			});
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);
export const fetchFeaturedGames = createAsyncThunk(
	"games/fetchFeaturedGames",
	async (_, thunkAPI) => {
		try {
			return await apiGet("/games/featured");
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);

export const fetchGames = createAsyncThunk(
	"games/fetchGames",
	async (params = {}, thunkAPI) => {
		try {
			const query = buildQueryString(params);
			return await apiGet("/games" + (query ? "?" + query : ""));
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);
export const fetchUserGames = createAsyncThunk(
	"games/fetchUserGames",
	async (params = {}, thunkAPI) => {
		try {
			const query = buildQueryString(params);
			return await apiGet("/games/user/me" + (query ? "?" + query : ""), null);
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
});
export const fetchGameFilters = createAsyncThunk(
	"games/fetchGameFilters",
	async (_, thunkAPI) => {
		try {
			return await apiGet("/games/filters");
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);
export const fetchGameBySlug = createAsyncThunk(
	"games/fetchGameBySlug",
	async (id, thunkAPI) => {
		try {
			return await apiGet("/games/by-slug/" + id);
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);

export const createGame = createAsyncThunk(
	"games/createGame",
	async (payload, thunkAPI) => {
		try {
			return await apiPost("/games", payload);
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);
export const updatePlayCount = createAsyncThunk(
	"games/updatePlayCount",
	async ({ id, userId }, thunkAPI) => {
		try {
			const query = userId ? `?userId=${userId}` : "";
			logger.info(`Updating play count for game ID: ${id} with user ID: ${userId}`);
			return await apiPut("/games/play-counter/" + id + query);
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);

export const uploadGameBuild = createAsyncThunk(
	"games/uploadGameBuild",
		async (formData, thunkAPI) => {
			try {
				thunkAPI.dispatch(setUploadProgress(0));

				return await apiPost("/games/upload-build", formData, {
					headers: {
						"Content-Type": "multipart/form-data",
						"Authorization":
						"Bearer " + (localStorage.getItem("token") || sessionStorage.getItem("token"))
					},
					onUploadProgress: (event) => {
						const progress = Math.round((event.loaded * 100) / (event.total || 1));
						thunkAPI.dispatch(setUploadProgress(progress));
					},
				});
			} catch (error) {
				return thunkAPI.rejectWithValue(error.message);
			}
		}
	);
export const generateGameSaveToken = createAsyncThunk(
	"games/generateGameSaveToken",
	async (gameId , thunkAPI) => {
		try {
			if(!gameId){
				throw new Error("Game ID is required to generate save token");
			}
			return await apiPost(`/games/create-game-token/${gameId}`,{});
		} catch (error) {
			return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
		}
	}
);

export const updateGame = createAsyncThunk(
	"games/updateGame",
	async ({ id, payload }, thunkAPI) => {
		try {
			thunkAPI.dispatch(setUploadProgress(0));

			return await apiPut("/games/" + id, payload, {
				headers: {
					"Content-Type": "multipart/form-data",
					"Authorization":
					"Bearer " + (localStorage.getItem("token") || sessionStorage.getItem("token"))
				},
				onUploadProgress: (event) => {
					const progress = Math.round((event.loaded * 100) / (event.total || 1));
					thunkAPI.dispatch(setUploadProgress(progress));
				},
			});
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);

export const deleteGame = createAsyncThunk(
	"games/deleteGame",
	async (id, thunkAPI) => {
		try {
			await apiDelete("/games/" + id);
			return { id };
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);

export const toggleGameLike = createAsyncThunk(
	"games/toggleGameLike",
	async (id, thunkAPI) => {
		try {
			return await apiPut("/games/" + id + "/like");
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);

export const toggleGameFavorite = createAsyncThunk(
	"games/toggleGameFavorite",
	async (id, thunkAPI) => {
		try {
			return await apiPut("/games/" + id + "/favorite");
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);
export const fetchMyFavoriteGames = createAsyncThunk(
	"games/fetchMyFavoriteGames",
	async (params = {}, thunkAPI) => {
		try {
			const query = buildQueryString(params);
			return await apiGet("/games/favorites/me", {
				headers: {
					Authorization:
					"Bearer " + (localStorage.getItem("token") || sessionStorage.getItem("token"))
				},
				params: query,
			});
			// NOTE: query params are already in URL via buildQueryString above.
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);
export const fetchRecentlyPlayedGames = createAsyncThunk(
	"games/fetchRecentlyPlayedGames",
	async (params = {}, thunkAPI) => {
		try {
			const query = buildQueryString(params);
			return await apiGet("/games/recently-played/me" + (query ? "?" + query : ""));
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);


export const addGameComment = createAsyncThunk(
	"games/addGameComment",
	async ({ id, content }, thunkAPI) => {
		try {
			return await apiPost("/games/" + id + "/comments", { content },{
				headers: {
					Authorization:
					"Bearer " + (localStorage.getItem("token") || sessionStorage.getItem("token"))
				}
			});
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);

export const toggleCommentLike = createAsyncThunk(
	"games/toggleCommentLike",
	async ({ id, commentId }, thunkAPI) => {
		try {
			return await apiPut("/games/" + id + "/comments/" + commentId + "/like");
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);

export const addCommentReply = createAsyncThunk(
	"games/addCommentReply",
	async ({ id, commentId, content }, thunkAPI) => {
		try {
			return await apiPost("/games/" + id + "/comments/" + commentId + "/replies", { content });
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);

export const toggleReplyLike = createAsyncThunk(
	"games/toggleReplyLike",
	async ({ id, commentId, replyId }, thunkAPI) => {
		try {
			return await apiPut("/games/" + id + "/comments/" + commentId + "/replies/" + replyId + "/like");
		} catch (error) {
			return thunkAPI.rejectWithValue(error.message);
		}
	}
);

const initialState = {
	games: [],
	selectedGame: null,
	updatedGameId:null,
	recommendedGames: [],
	userGames:[],
	featuredGames:[],
	featuredLoading: false,
	favoriteGames: [],
	recentlyPlayedGames: [],
	userGamesPagination: {
		page: 1,
		limit: 9,
		totalGames: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPrevPage: false,
	},
	favoriteGamesPagination: {
		page: 1,
		limit: 9,
		totalGames: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPrevPage: false,
	},
	recentGamesPagination: {
		page: 1,
		limit: 9,
		totalGames: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPrevPage: false,
	},
	pagination: {
		page: 1,
		limit: 8,
		totalGames: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPrevPage: false,
	},
	filters: {
		page: 1,
		limit: 8,
		genre: "All",
		search: "",
		sort: "",
	},
	isLoading: false,
	loadingMore: false,
	error: null,
	uploadSuccess: false,
	uploadProgress: 0,
};
const syncSelectedGame = (state, action) => {
	const updatedGame = action.payload.game || action.payload;
	state.selectedGame = updatedGame;
	/* state.games = state.games.map((item) =>
		item._id === updatedGame._id || item.id === updatedGame.id ? updatedGame : item
	); */
	if(action.payload?.recommendedGames){
		if(action.payload.recommendedGames.length > 0){
			state.recommendedGames = action.payload.recommendedGames;
		}
	}
};

const homePageSlice = createSlice({
	name: "games",
	initialState,
	reducers: {
		setFilters(state, action) {
			state.filters = {
				...state.filters,
				...action.payload,
			};
		},
		clearUpdatedGameId(state){
			state.updatedGameId = null;
		},
		clearSelectedGame(state) {
			state.selectedGame = null;
		},
		clearGameError(state) {
			state.error = null;
		},
		clearUploadSuccess(state) {
			state.uploadSuccess = false;
		},
		setUploadProgress(state, action) {
			state.uploadProgress = action.payload;
		},
		resetUploadProgress(state) {
			state.uploadProgress = 0;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchUserGames.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchUserGames.fulfilled, (state, action) => {
				state.isLoading = false;
				state.userGames = action.payload.games || action.payload;
				if (action.payload.pagination) {
					state.userGamesPagination = action.payload.pagination;
				}
			})
			.addCase(fetchUserGames.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})
			.addCase(toggleGameLike.fulfilled, syncSelectedGame)
			.addCase(toggleGameFavorite.fulfilled, syncSelectedGame)
			.addCase(addGameComment.fulfilled, syncSelectedGame)
			.addCase(toggleCommentLike.fulfilled, syncSelectedGame)
			.addCase(addCommentReply.fulfilled, syncSelectedGame)
			.addCase(toggleReplyLike.fulfilled, syncSelectedGame)
			.addCase(fetchFeaturedGames.pending, (state) => {
				state.featuredLoading = true;
				state.error = null;
			})
			.addCase(fetchFeaturedGames.fulfilled, (state, action) => {
				state.featuredLoading = false;
				state.featuredGames = action.payload.games || action.payload;
			})	
			.addCase(fetchFeaturedGames.rejected, (state, action) => {
				state.featuredLoading = false;
				state.error = action.payload;
			})

			.addCase(updateGameBuild.fulfilled,(state,action)=>{
				const gameId = action.payload.game?.slug || action.payload.game?.slug || action.payload.slug || action.payload.id;
				logger.info(`Game build updated successfully for game ID:`,action.payload);
				state.selectedGame = action.payload.game || action.payload;
				state.updatedGameId = gameId;
			})
			
			.addCase(generateGameSaveToken.fulfilled, (state, action) => {
				if (state.selectedGame) {
					state.selectedGame.gameDataToken = action.payload.token || action.payload;
				}
			})
			.addCase(fetchMyFavoriteGames.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchMyFavoriteGames.fulfilled, (state, action) => {
				state.isLoading = false;
				state.favoriteGames = action.payload.games || action.payload;
				if (action.payload.pagination) {
					state.favoriteGamesPagination = action.payload.pagination;
				}
			})
			.addCase(fetchMyFavoriteGames.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})
			.addCase(fetchRecentlyPlayedGames.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchRecentlyPlayedGames.fulfilled, (state, action) => {
				state.isLoading = false;
				logger.debug("Fetched recently played games:", action.payload);
				state.recentlyPlayedGames = action.payload.games || action.payload;
				if (action.payload.pagination) {
					state.recentGamesPagination = action.payload.pagination;
				}
			})
			.addCase(fetchRecentlyPlayedGames.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})
			.addCase(fetchGames.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})

			.addCase(fetchGames.fulfilled, (state, action) => {
				state.isLoading = false;

				const newGames = action.payload.games || action.payload.items || [];

				// ✅ ALWAYS replace (no infinite merge)
				state.games = newGames;

				state.pagination = {
					page: action.payload.page ?? action.payload.pagination?.page ?? 1,
					limit: action.payload.limit ?? action.payload.pagination?.limit ?? 12,
					totalGames:
						action.payload.totalGames ?? action.payload.pagination?.total ?? 0,
					totalPages:
						action.payload.totalPages ??
						action.payload.pagination?.totalPages ??
						0,
					hasNextPage:
						action.payload.hasNextPage ??
						action.payload.pagination?.hasNextPage ??
						false,
					hasPrevPage:
						action.payload.hasPrevPage ??
						action.payload.pagination?.hasPrevPage ??
						false,
				};
			})
			.addCase(updatePlayCount.pending,(state)=>{
				state.error = null;
				state.isLoading = true;
			})
			.addCase(updatePlayCount.fulfilled,(state,action)=>{
				state.isLoading = false;
				const updatedGame = action.payload?.game;
				if(updatedGame){
					state.games = state.games.map((game) =>
						game._id === updatedGame._id
							? updatedGame
							: game
					);
				}

			})
			.addCase(fetchGameFilters.pending, (state) => {
				state.error = null;
			})
			.addCase(fetchGameFilters.fulfilled, (state, action) => {
				state.filtersOptions = action.payload.filters || action.payload;
				state.filtersLoaded = true;
			})
			.addCase(fetchGameFilters.rejected, (state, action) => {
				state.error = action.payload;
				state.filtersLoaded = false;
			})
			.addCase(fetchGames.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})
			.addCase(fetchGameBySlug.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchGameBySlug.fulfilled, (state, action) => {
				state.isLoading = false;
				state.selectedGame = action.payload.game || action.payload;
				state.recommendedGames = action.payload.recommendedGames || [];
			})
			.addCase(fetchGameBySlug.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})
			.addCase(createGame.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(createGame.fulfilled, (state, action) => {
				state.isLoading = false;
				const createdGame = action.payload.game || action.payload;
				state.games.unshift(createdGame);
			})
			.addCase(createGame.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})
			.addCase(uploadGameBuild.pending, (state) => {
				state.isLoading = true;
				state.error = null;
				state.uploadSuccess = false;
				state.uploadProgress = 0;
			})
			.addCase(uploadGameBuild.fulfilled, (state, action) => {
				state.isLoading = false;
				state.uploadSuccess = true;
				state.uploadProgress = 100;
				const createdGame = action.payload.game || action.payload;
				state.games.unshift(createdGame);
				state.selectedGame = createdGame;
				state.updatedGameId = createdGame.slug || createdGame.slug;
			})
			.addCase(uploadGameBuild.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
				state.uploadSuccess = false;
				state.uploadProgress = 0;
			})
			.addCase(updateGame.pending, (state) => {
				state.isLoading = true;
				state.error = null;
				state.uploadProgress = 0;
			})
			.addCase(updateGame.fulfilled, (state, action) => {
				state.isLoading = false;
				state.uploadProgress = 100;
				const updatedGame = action.payload.game || action.payload;
				state.games = state.games.map((game) =>
				game._id === updatedGame._id || game.id === updatedGame.id
					? updatedGame
					: game
				);
				if (
				state.selectedGame &&
				(state.selectedGame._id === updatedGame._id ||
					state.selectedGame.id === updatedGame.id)
				) {
					state.selectedGame = updatedGame;
				}
			})
			.addCase(updateGame.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
				state.uploadProgress = 0;
			})
			.addCase(deleteGame.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(deleteGame.fulfilled, (state, action) => {
				state.isLoading = false;
				state.games = state.games.filter(
				(game) => game._id !== action.payload.id && game.id !== action.payload.id
				);
				if (
				state.selectedGame &&
				(state.selectedGame._id === action.payload.id ||
					state.selectedGame.id === action.payload.id)
				) {
				state.selectedGame = null;
				}
			})
			.addCase(deleteGame.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			});
	},
});

export const {
	setFilters,
	clearSelectedGame,
	clearGameError,
	clearUploadSuccess,
	setUploadProgress,
	resetUploadProgress,
	clearUpdatedGameId,
} = homePageSlice.actions;

export default homePageSlice.reducer;